import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlGroup,
  Radio,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  Alert,
  Divider,
  Tooltip,
  Snackbar,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Block as BlockIcon,
  CheckCircle as ActiveIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import firebaseService from '../services/FirebaseService';
import { ShareLink, ShareLinkService, SHARE_PERMISSIONS } from '../models/ShareLink';

const ShareLinkManager = ({ open, onClose, draftId = null, draftTitle = 'Draft', onSaveDraft = null }) => {
  const [shareLinks, setShareLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);

  // Create new link state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newLinkExpiration, setNewLinkExpiration] = useState(7); // Default: 7 days
  const [newLinkPassword, setNewLinkPassword] = useState('');
  const [newLinkAllowedEmails, setNewLinkAllowedEmails] = useState('');

  // Edit dialog removed - VIEW permission is fixed, users can only toggle active/inactive

  useEffect(() => {
    if (open) {
      loadShareLinks();
    }
  }, [open]);

  const loadShareLinks = async () => {
    setLoading(true);
    setError(null);
    try {
      // Load ALL share links for the current user
      const links = await firebaseService.getAllShareLinksForUser();
      setShareLinks(links);
    } catch (err) {
      setError('Failed to load share links: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateShareLink = async () => {
    try {
      if (!draftId) {
        setError('Draft ID not found. Please close this dialog and try again.');
        return;
      }

      const user = firebaseService.getCurrentUser();

      // Password is REQUIRED for security
      if (!newLinkPassword || newLinkPassword.trim() === '') {
        setError('Password is required for security');
        return;
      }

      const options = {
        permission: SHARE_PERMISSIONS.VIEW, // Always VIEW - simple and secure
        expiresAt: newLinkExpiration > 0
          ? ShareLinkService.getExpirationTimestamp(newLinkExpiration)
          : null,
        password: newLinkPassword,
        allowedEmails: newLinkAllowedEmails
          ? newLinkAllowedEmails.split(',').map(e => e.trim()).filter(e => e)
          : [],
      };

      const shareLink = ShareLinkService.createShareLink(
        draftId,
        draftTitle,
        user.uid,
        user.displayName || user.email,
        options
      );

      await firebaseService.createShareLink(shareLink);

      // Copy to clipboard
      const shareUrl = shareLink.getShareUrl(window.location.origin);
      await navigator.clipboard.writeText(shareUrl);

      // Save the draft to ensure latest changes are persisted
      if (onSaveDraft) {
        onSaveDraft();
      }

      setShowCreateDialog(false);
      resetCreateForm();
      await loadShareLinks();

      // Show success snackbar and close the main dialog
      setShowSuccessSnackbar(true);
      onClose();
    } catch (err) {
      setError('Failed to create share link: ' + err.message);
    }
  };

  const resetCreateForm = () => {
    setNewLinkExpiration(7); // Reset to default 7 days
    setNewLinkPassword('');
    setNewLinkAllowedEmails('');
  };

  const handleCopyLink = async (shareLink) => {
    try {
      const shareUrl = shareLink.getShareUrl
        ? shareLink.getShareUrl(window.location.origin)
        : `${window.location.origin}/shared/${shareLink.shareToken}`;
      await navigator.clipboard.writeText(shareUrl);
      setSuccess('Link copied to clipboard!');
    } catch (err) {
      setError('Failed to copy link: ' + err.message);
    }
  };

  const handleToggleActive = async (shareLink) => {
    try {
      const newStatus = !shareLink.isActive;
      await firebaseService.updateShareLink(shareLink.shareToken, {
        isActive: newStatus,
      });
      setSuccess(newStatus ? 'Link reactivated' : 'Link revoked');
      await loadShareLinks();
    } catch (err) {
      setError('Failed to update link: ' + err.message);
    }
  };

  const handleDeleteLink = async (shareToken) => {
    if (window.confirm('Delete this share link? This cannot be undone.')) {
      try {
        await firebaseService.deleteShareLink(shareToken);
        setSuccess('Share link deleted');
        await loadShareLinks();
      } catch (err) {
        setError('Failed to delete link: ' + err.message);
      }
    }
  };

  // Edit functionality removed - permissions are fixed to VIEW only

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const getPermissionLabel = () => {
    return 'View Only'; // Simplified - only one permission level
  };

  const getPermissionColor = () => {
    return 'info'; // Always blue for View Only
  };

  const getAnalyticsSummary = (shareLink) => {
    const analytics = ShareLinkService.getAnalytics(shareLink);
    return `${analytics.totalViews} views • ${analytics.uniqueViewers} unique`;
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h6">All Share Links</Typography>
              <Typography variant="caption" color="text.secondary">
                Manage all your shared draft links
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<LinkIcon />}
              onClick={() => setShowCreateDialog(true)}
            >
              Create New Link
            </Button>
          </Box>
        </DialogTitle>

        <DialogContent>
          {error && (
            <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {loading ? (
            <Typography>Loading share links...</Typography>
          ) : shareLinks.length === 0 ? (
            <Alert severity="info">
              No share links yet. Create one to share this draft with others.
            </Alert>
          ) : (
            <List>
              {shareLinks.map((shareLink) => {
                const isExpired = shareLink.expiresAt && Date.now() > shareLink.expiresAt;
                const shareUrl = `${window.location.origin}/shared/${shareLink.shareToken}`;

                return (
                  <ListItem
                    key={shareLink.shareToken}
                    sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 2,
                      bgcolor: shareLink.isActive ? 'background.paper' : 'action.disabledBackground',
                      flexDirection: 'column',
                      alignItems: 'stretch',
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="start" width="100%">
                      <Box flex={1}>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          {shareLink.isActive ? (
                            <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                              ● Active
                            </Typography>
                          ) : (
                            <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                              ● Revoked
                            </Typography>
                          )}
                          {isExpired && (
                            <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 'bold', ml: 1 }}>
                              (Expired)
                            </Typography>
                          )}
                        </Box>

                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 'bold',
                            color: 'primary.main',
                            mb: 1,
                          }}
                        >
                          📄 {shareLink.title || 'Untitled Draft'}
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            bgcolor: 'action.hover',
                            p: 1,
                            borderRadius: 1,
                            wordBreak: 'break-all',
                          }}
                        >
                          {shareUrl}
                        </Typography>

                        <Box mt={1}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Created: {formatDate(shareLink.createdAt)} by {shareLink.createdByName}
                          </Typography>
                          {shareLink.expiresAt && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Expires: {formatDate(shareLink.expiresAt)}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary" display="block">
                            {getAnalyticsSummary(shareLink)}
                          </Typography>
                          {shareLink.lastAccessedAt && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Last accessed: {formatDate(shareLink.lastAccessedAt)}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      <Box display="flex" gap={0.5}>
                        <Tooltip title="Copy link">
                          <IconButton
                            size="small"
                            onClick={() => handleCopyLink(shareLink)}
                          >
                            <CopyIcon />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title={shareLink.isActive ? 'Revoke access' : 'Reactivate'}>
                          <IconButton
                            size="small"
                            onClick={() => handleToggleActive(shareLink)}
                            color={shareLink.isActive ? 'error' : 'success'}
                          >
                            {shareLink.isActive ? <BlockIcon /> : <ActiveIcon />}
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Delete link">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteLink(shareLink.shareToken)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </ListItem>
                );
              })}
            </List>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Create Share Link Dialog */}
      <Dialog
        open={showCreateDialog}
        onClose={() => {
          setShowCreateDialog(false);
          resetCreateForm();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Share Link</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Share links are password-protected and allow view-only access. Users can edit in the simulator but must log in to save changes.
          </Alert>

          <Box
            component="form"
            sx={{ mt: 2 }}
            onSubmit={(e) => {
              e.preventDefault();
              handleCreateShareLink();
            }}
          >
            <TextField
              fullWidth
              label="Password (Required)"
              type="password"
              value={newLinkPassword}
              onChange={(e) => setNewLinkPassword(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleCreateShareLink();
                }
              }}
              margin="normal"
              required
              autoFocus
              helperText="Anyone with this password can view the draft"
            />

            <FormControl fullWidth margin="normal">
              <InputLabel>Link Expiration</InputLabel>
              <Select
                value={newLinkExpiration}
                onChange={(e) => setNewLinkExpiration(e.target.value)}
                label="Link Expiration"
              >
                <MenuItem value={0}>Never expires</MenuItem>
                <MenuItem value={1}>1 day</MenuItem>
                <MenuItem value={7}>7 days</MenuItem>
                <MenuItem value={30}>30 days</MenuItem>
                <MenuItem value={90}>90 days</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Allowed Emails (optional)"
              value={newLinkAllowedEmails}
              onChange={(e) => setNewLinkAllowedEmails(e.target.value)}
              margin="normal"
              placeholder="user1@example.com, user2@example.com"
              helperText="Comma-separated list. Leave empty to allow anyone with the link."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowCreateDialog(false);
              resetCreateForm();
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleCreateShareLink} variant="contained">
            Create Link
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={4000}
        onClose={() => setShowSuccessSnackbar(false)}
        message="✅ Share link created and copied to clipboard!"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
};

export default ShareLinkManager;
